import React, { useEffect, useRef, useState } from 'react'
import type { ColDef, GridOptions } from 'ag-grid-community'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import axios from 'lib/axios'
import { useAuthContext } from 'context/auth'
import { readinessErrorMessage } from 'lib/vacancy-readiness'
import Grid from 'components/TailwindUIToolkit/Grid/BaseGrid'

export type RentPriceSummary = {
  vacancyPeriodId: string
  userId: string | undefined
  listedRent: string | null
  visible: boolean
  loading: boolean
  unavailable: boolean
}

type Price = {
  id: string
  listed_rent: string
  base_rent: string
  actor_name: string
  reason: string | null
  created_at: string
}
type Prices = {
  ineligibility_message: string | null
  editable: boolean
  current: Price | null
  items: Price[]
  total_count: number
  page: number
  per_page: number
  history_scope?: 'vacancy' | 'home'
}
type Command = {
  client_key: string
  expected_price_id: string | null
  listed_rent: string
  base_rent: string
  reason: string
}
const money = (value?: string) =>
  value && Number.isFinite(Number(value))
    ? Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : value || 'Not set'
const status = (error: unknown) =>
  (error as { statusCode?: number; status?: number })?.statusCode ??
  (error as { status?: number })?.status
const PAGE_SIZE = 25
const historyColumns: ColDef<Price>[] = [
  {
    field: 'created_at',
    headerName: 'Changed',
    width: 205,
    valueFormatter: ({ value }) =>
      new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
  },
  {
    field: 'listed_rent',
    headerName: 'Listed rent',
    width: 130,
    valueFormatter: ({ value }) => money(value)
  },
  {
    field: 'base_rent',
    headerName: 'Base rent',
    width: 120,
    valueFormatter: ({ value }) => money(value)
  },
  { field: 'actor_name', headerName: 'Changed by', width: 160, tooltipField: 'actor_name' },
  {
    field: 'reason',
    headerName: 'Note',
    minWidth: 190,
    flex: 1,
    tooltipField: 'reason',
    valueFormatter: ({ value }) => value || 'No note recorded.'
  }
]
const historyOptions: GridOptions<Price> = {
  sideBar: false,
  headerHeight: 36,
  rowHeight: 40,
  suppressScrollOnNewData: true,
  enableBrowserTooltips: true,
  rowSelection: 'single'
}
const historyColumnDefaults: ColDef<Price> = {
  sortable: false,
  filter: false,
  resizable: true,
  autoHeight: false,
  wrapText: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellStyle: { lineHeight: '38px', display: 'block' }
}

export default function RentPricePanel({
  vacancyPeriodId,
  disabled,
  onEditingChange,
  onSummaryChange,
  onSaved
}: {
  vacancyPeriodId: string
  disabled: boolean
  onEditingChange: (value: boolean) => void
  onSummaryChange?: (summary: RentPriceSummary) => void
  onSaved?: () => void
}) {
  const { firebaseUser } = useAuthContext()
  const client = useQueryClient()
  const [page, setPage] = useState(1)
  const [historyScope, setHistoryScope] = useState<'vacancy' | 'home'>('vacancy')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [selectedNote, setSelectedNote] = useState<Price | null>(null)
  const historyPage = useRef<number | null>(null)
  const [lastPrices, setLastPrices] = useState<{ identity: string; data: Prices } | null>(null)
  const path = `/admin/vacancies/${encodeURIComponent(vacancyPeriodId)}/rent-prices`
  const identity = `${firebaseUser?.uid}:${vacancyPeriodId}`
  const key = ['vacancy-rent-prices', firebaseUser?.uid, vacancyPeriodId]
  const query = useQuery<Prices>({
    queryKey: [...key, historyScope, PAGE_SIZE, page],
    queryFn: async () => {
      const params = { page, per_page: PAGE_SIZE }
      let data: Prices
      try {
        data = (
          await axios.get(path, {
            params: { ...params, history_scope: historyScope }
          })
        ).data
      } catch (caught) {
        // During a rolling deployment an older API only supports home history.
        if (![400, 422].includes(status(caught) ?? 0)) throw caught
        data = (await axios.get(path, { params })).data
      }
      return {
        ...data,
        history_scope: data.history_scope ?? (historyScope === 'home' ? 'home' : undefined)
      }
    },
    enabled: !!firebaseUser,
    keepPreviousData: true,
    retry: false
  })
  useEffect(() => {
    if (query.data && !query.isPreviousData) setLastPrices({ identity, data: query.data })
  }, [identity, query.data, query.isPreviousData])
  const previousPrices = lastPrices?.identity === identity ? lastPrices.data : undefined
  const prices = query.isPreviousData ? previousPrices : query.data ?? previousPrices
  const priceAccessDenied = query.isError && status(query.error) === 403
  useEffect(() => {
    onSummaryChange?.({
      vacancyPeriodId,
      userId: firebaseUser?.uid,
      listedRent: priceAccessDenied ? null : prices?.current?.listed_rent ?? null,
      visible: !priceAccessDenied,
      loading: query.isLoading,
      unavailable: query.isError
    })
  }, [
    vacancyPeriodId,
    firebaseUser?.uid,
    prices?.current?.listed_rent,
    priceAccessDenied,
    query.isLoading,
    query.isError,
    onSummaryChange
  ])
  const history = prices?.history_scope === historyScope ? prices : undefined
  const [draft, setDraft] = useState<Omit<Command, 'client_key'> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const sending = useRef(false)
  const attempt = useRef<{ fingerprint: string; command: Command } | null>(null)
  const editing = !!draft
  useEffect(() => {
    onEditingChange(editing)
    return () => onEditingChange(false)
  }, [editing, onEditingChange])
  const update = (patch: Partial<Omit<Command, 'client_key'>>) => {
    attempt.current = null
    setDraft((value) => (value ? { ...value, ...patch } : value))
  }
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!draft || sending.current || conflict) return
    const fingerprint = JSON.stringify(draft)
    if (attempt.current?.fingerprint !== fingerprint) {
      if (
        !draft.reason.trim() ||
        ![draft.listed_rent, draft.base_rent].every(
          (value) => /^(0|[1-9]\d{0,7})(\.\d{1,2})?$/.test(value) && Number(value) > 0
        )
      ) {
        setError('Enter positive rent amounts with at most two decimal places and add a note.')
        return
      }
      attempt.current = {
        fingerprint,
        command: { ...draft, reason: draft.reason.trim(), client_key: uuid() }
      }
    }
    sending.current = true
    setSaving(true)
    setError(null)
    try {
      await axios.post(`${path}/actions`, attempt.current.command)
      setDraft(null)
      attempt.current = null
      setSaved(true)
      historyPage.current = null
      setPage(1)
      await client.invalidateQueries({ queryKey: key })
      onSaved?.()
    } catch (caught) {
      setError(readinessErrorMessage(caught))
      setConflict(status(caught) === 409)
    } finally {
      sending.current = false
      setSaving(false)
    }
  }
  if (query.isError && status(query.error) === 403) return null
  return (
    <Stack spacing={3} borderWidth={1} borderRadius='lg' p={4}>
      <Heading size='sm'>Rent prices</Heading>
      <Text fontSize='sm' color='gray.600'>
        Asking prices for this home. Existing application and lease amounts are kept.
      </Text>
      {query.isLoading && <Text role='status'>Loading prices…</Text>}
      {query.isError && (
        <Alert status='error' display='block'>
          {readinessErrorMessage(query.error)}{' '}
          <Button size='sm' onClick={() => query.refetch()}>
            Retry prices
          </Button>
        </Alert>
      )}
      {saved && <Alert status='success'>Rent prices saved.</Alert>}
      {prices && (
        <>
          {prices.ineligibility_message && (
            <Text fontSize='sm' color='gray.600'>
              {prices.ineligibility_message}
            </Text>
          )}
          <HStack spacing={6} flexWrap='wrap'>
            <Text>
              Listed rent: <b>{money(prices.current?.listed_rent)}</b>
            </Text>
            <Text>
              Base rent: <b>{money(prices.current?.base_rent)}</b>
            </Text>
          </HStack>
          {!draft && prices.editable && (
            <Button
              alignSelf='start'
              size='sm'
              variant='outline'
              isDisabled={disabled || query.isError}
              onClick={() => {
                const current = prices.current
                setDraft({
                  expected_price_id: current?.id ?? null,
                  listed_rent:
                    current && Number.isFinite(Number(current.listed_rent))
                      ? Number(current.listed_rent).toFixed(2)
                      : '',
                  base_rent:
                    current && Number.isFinite(Number(current.base_rent))
                      ? Number(current.base_rent).toFixed(2)
                      : '',
                  reason: ''
                })
                setSaved(false)
                setError(null)
                setConflict(false)
              }}
            >
              Edit prices
            </Button>
          )}
          {draft && (
            <Box
              as='form'
              onSubmit={save}
              p={3}
              borderWidth={1}
              borderColor='teal.400'
              borderRadius='md'
            >
              <Stack spacing={3}>
                {(['listed_rent', 'base_rent'] as const).map((field) => (
                  <FormControl isRequired key={field}>
                    <FormLabel htmlFor={field}>
                      {field === 'listed_rent' ? 'Listed rent' : 'Base rent'} ($ per month)
                    </FormLabel>
                    <Input
                      id={field}
                      inputMode='decimal'
                      value={draft[field]}
                      isDisabled={saving}
                      onChange={(event) => update({ [field]: event.target.value })}
                    />
                  </FormControl>
                ))}
                <FormControl isRequired>
                  <FormLabel htmlFor='rent-price-note'>Reason for price change</FormLabel>
                  <Textarea
                    id='rent-price-note'
                    maxLength={2000}
                    value={draft.reason}
                    isDisabled={saving}
                    onChange={(event) => update({ reason: event.target.value })}
                  />
                </FormControl>
                <Text fontSize='sm'>
                  Save listed rent {money(draft.listed_rent)} and base rent {money(draft.base_rent)}
                  .
                </Text>
                {error && (
                  <Alert status='error' display='block'>
                    {error}
                    {conflict && (
                      <Button
                        mt={2}
                        size='sm'
                        isLoading={query.isFetching}
                        onClick={async () => {
                          attempt.current = null
                          const refreshed = await query.refetch()
                          if (refreshed.data && !refreshed.isError) {
                            update({ expected_price_id: refreshed.data.current?.id ?? null })
                            setConflict(false)
                            setError(
                              'Latest saved prices are shown above. Review your entries before saving again.'
                            )
                          }
                        }}
                      >
                        Refresh prices and keep my entries
                      </Button>
                    )}
                  </Alert>
                )}
                {error && attempt.current && !conflict && (
                  <Text fontSize='sm'>
                    Retrying sends the same save request. Your entries have been kept.
                  </Text>
                )}
                <HStack>
                  <Button
                    variant='ghost'
                    isDisabled={saving}
                    onClick={() => {
                      setDraft(null)
                      attempt.current = null
                      setError(null)
                      setConflict(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' colorScheme='teal' isLoading={saving} isDisabled={conflict}>
                    Save prices
                  </Button>
                </HStack>
              </Stack>
            </Box>
          )}
          <Button
            variant='link'
            alignSelf='start'
            size='sm'
            aria-expanded={historyOpen}
            aria-controls='rent-price-history'
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            {historyOpen ? 'Hide' : 'Show'} price history
            {history ? ` (${history.total_count})` : ''}
          </Button>
          {historyOpen && (
            <Stack id='rent-price-history' spacing={2} minW={0}>
              <Checkbox
                isChecked={historyScope === 'home'}
                onChange={(event) => {
                  setHistoryScope(event.target.checked ? 'home' : 'vacancy')
                  setPage(1)
                  setSelectedNote(null)
                  historyPage.current = null
                }}
              >
                Show all home price history
              </Checkbox>
              {!prices.history_scope && !query.isFetching && !query.isError && (
                <Text fontSize='sm' color='gray.600'>
                  Vacancy-specific history is temporarily unavailable. Select “Show all home price
                  history” to view this home’s prices.
                </Text>
              )}
              <Text fontSize='sm' color='gray.600'>
                {historyScope === 'home'
                  ? 'All home price history, including previous vacancies.'
                  : 'Price changes linked to this vacancy.'}
              </Text>

              <Box
                role='region'
                aria-label='Rent price history'
                aria-busy={query.isFetching}
                minW={0}
              >
                <Grid<Price>
                  columns={historyColumns}
                  rowData={history?.items ?? []}
                  containerStyle={{ height: 248, width: '100%' }}
                  defaultColDefOverrides={historyColumnDefaults}
                  optionOverrides={historyOptions}
                  getRowId={({ data }) => data.id}
                  onRowDataUpdated={({ api }) => {
                    if (history?.items.length && historyPage.current !== history.page) {
                      api.ensureIndexVisible(0, 'top')
                      historyPage.current = history.page
                    }
                  }}
                  onRowClicked={({ data }) => setSelectedNote(data ?? null)}
                  onCellKeyDown={({ data, event }) => {
                    if ((event as KeyboardEvent)?.key === 'Enter') setSelectedNote(data ?? null)
                  }}
                  overlayNoRowsTemplate={
                    query.isFetching
                      ? 'Loading price changes…'
                      : historyScope === 'vacancy'
                      ? 'No price changes linked to this vacancy.'
                      : 'No price changes recorded.'
                  }
                />
              </Box>
              <Box role='status' minH={5} fontSize='sm' color='gray.600'>
                {query.isFetching
                  ? 'Loading price changes…'
                  : history
                  ? `${
                      history.total_count ? (history.page - 1) * history.per_page + 1 : 0
                    }–${Math.min(history.page * history.per_page, history.total_count)} of ${
                      history.total_count
                    } changes`
                  : 'No price changes loaded.'}
              </Box>
              {selectedNote && (
                <Box borderWidth={1} borderRadius='md' p={2} fontSize='sm'>
                  <Text fontWeight='semibold'>
                    Selected change · {new Date(selectedNote.created_at).toLocaleDateString()} ·{' '}
                    {selectedNote.actor_name}
                  </Text>
                  <Text whiteSpace='pre-wrap' overflowWrap='anywhere' maxH='80px' overflowY='auto'>
                    {selectedNote.reason || 'No note recorded.'}
                  </Text>
                </Box>
              )}
              {history && history.total_count > PAGE_SIZE && (
                <HStack>
                  <Button
                    size='sm'
                    isDisabled={history.page === 1 || query.isFetching || query.isError}
                    onClick={() => setPage(history.page - 1)}
                  >
                    Newer
                  </Button>
                  <Text fontSize='sm'>
                    Page {history.page} of {Math.ceil(history.total_count / history.per_page)}
                  </Text>
                  <Button
                    size='sm'
                    isDisabled={
                      history.page * history.per_page >= history.total_count ||
                      query.isFetching ||
                      query.isError
                    }
                    onClick={() => setPage(history.page + 1)}
                  >
                    Older
                  </Button>
                </HStack>
              )}
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}
